import React, { useState, useEffect } from 'react';
import {
  Flag,
  Plus,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Calendar,
  User,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Clock,
} from 'lucide-react';
import api, {
  getFeatureFlags,
  toggleGlobalFlag,
  getAccountEntitlements,
  createAccountEntitlement,
  updateAccountEntitlement,
  deleteAccountEntitlement,
} from '../services/api';
import './FeatureFlagsSection.css';

/**
 * Feature Flags & Entitlements Section for Platform Admin.
 * Allows superusers to manage feature flags and account-level entitlements.
 */
export const FeatureFlagsSection = () => {
  const [featureFlags, setFeatureFlags] = useState([]);
  const [entitlements, setEntitlements] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entitlementsLoading, setEntitlementsLoading] = useState(false);
  const [showEntitlementModal, setShowEntitlementModal] = useState(false);
  const [editingEntitlement, setEditingEntitlement] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchFeatureFlags();
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (selectedAccount) {
      fetchEntitlements(selectedAccount.id);
    } else {
      setEntitlements([]);
    }
  }, [selectedAccount]);

  const fetchFeatureFlags = async () => {
    try {
      setLoading(true);
      const response = await getFeatureFlags();
      setFeatureFlags(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to load feature flags:', err);
      setError(err.response?.data?.error || 'Failed to load feature flags');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await api.get('/platform/tenants/');
      setAccounts(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to load accounts:', err);
    }
  };

  const fetchEntitlements = async (accountId) => {
    try {
      setEntitlementsLoading(true);
      const response = await getAccountEntitlements({ account: accountId });
      setEntitlements(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to load entitlements:', err);
      setError(err.response?.data?.error || 'Failed to load entitlements');
    } finally {
      setEntitlementsLoading(false);
    }
  };

  const handleToggleGlobal = async (flagId) => {
    try {
      await toggleGlobalFlag(flagId);
      await fetchFeatureFlags();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to toggle global flag');
    }
  };

  const handleDeleteEntitlement = async (entitlementId) => {
    if (!window.confirm('Are you sure you want to delete this entitlement?')) {
      return;
    }

    try {
      await deleteAccountEntitlement(entitlementId);
      if (selectedAccount) {
        await fetchEntitlements(selectedAccount.id);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete entitlement');
    }
  };

  const handleOpenEntitlementModal = (entitlement = null) => {
    setEditingEntitlement(entitlement);
    setShowEntitlementModal(true);
  };

  const handleCloseEntitlementModal = () => {
    setShowEntitlementModal(false);
    setEditingEntitlement(null);
  };

  const handleSaveEntitlement = async (data) => {
    try {
      if (editingEntitlement) {
        await updateAccountEntitlement(editingEntitlement.id, data);
      } else {
        await createAccountEntitlement({
          ...data,
          account: selectedAccount.id,
        });
      }
      handleCloseEntitlementModal();
      if (selectedAccount) {
        await fetchEntitlements(selectedAccount.id);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save entitlement');
    }
  };

  const getSourceBadgeClass = (source) => {
    const classes = {
      early_access: 'entitlement-source-early-access',
      promo: 'entitlement-source-promo',
      paid: 'entitlement-source-paid',
      admin_override: 'entitlement-source-admin',
    };
    return classes[source] || 'entitlement-source-admin';
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString();
  };

  const isExpired = (entitlement) => {
    if (!entitlement.expires_at) return false;
    return new Date(entitlement.expires_at) < new Date();
  };

  return (
    <div className="admin-section-content">
      <div className="admin-section-header">
        <div>
          <h2>Feature Flags & Entitlements</h2>
          <p className="admin-section-description">
            Manage global feature flags and account-level feature entitlements
          </p>
        </div>
      </div>

      {error && (
        <div className="admin-error-banner">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Feature Flags List */}
      <div className="feature-flags-section">
        <h3 className="feature-flags-subtitle">Global Feature Flags</h3>
        {loading ? (
          <div className="admin-loading">
            <div className="loading-spinner">Loading feature flags...</div>
          </div>
        ) : featureFlags.length === 0 ? (
          <div className="admin-empty-state">
            <Flag size={48} />
            <p>No feature flags found</p>
          </div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-data-table">
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Description</th>
                  <th>Global Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {featureFlags.map((flag) => (
                  <tr key={flag.id}>
                    <td>
                      <div className="admin-table-cell-primary">
                        <code className="feature-flag-key">{flag.key}</code>
                      </div>
                    </td>
                    <td>{flag.description || '-'}</td>
                    <td>
                      <button
                        className={`feature-flag-toggle ${flag.is_globally_enabled ? 'enabled' : 'disabled'}`}
                        onClick={() => handleToggleGlobal(flag.id)}
                        title={flag.is_globally_enabled ? 'Disable globally' : 'Enable globally'}
                      >
                        {flag.is_globally_enabled ? (
                          <>
                            <ToggleRight size={20} />
                            <span>Enabled</span>
                          </>
                        ) : (
                          <>
                            <ToggleLeft size={20} />
                            <span>Disabled</span>
                          </>
                        )}
                      </button>
                    </td>
                    <td>
                      <div className="admin-action-buttons">
                        <button
                          className="admin-btn admin-btn-secondary"
                          onClick={() => {
                            // Could add edit functionality here
                            alert('Edit feature flag functionality coming soon');
                          }}
                          title="Edit feature flag"
                        >
                          <Edit size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Account Entitlements */}
      <div className="account-entitlements-section">
        <div className="entitlements-header">
          <h3 className="feature-flags-subtitle">Account Entitlements</h3>
          <div className="entitlements-controls">
            <select
              className="account-select"
              value={selectedAccount?.id || ''}
              onChange={(e) => {
                const account = accounts.find((a) => a.id === e.target.value);
                setSelectedAccount(account || null);
              }}
            >
              <option value="">Select an account...</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.firm_name || account.name} ({account.email})
                </option>
              ))}
            </select>
            {selectedAccount && (
              <button
                className="admin-btn admin-btn-primary"
                onClick={() => handleOpenEntitlementModal()}
              >
                <Plus size={16} />
                Add Entitlement
              </button>
            )}
          </div>
        </div>

        {!selectedAccount ? (
          <div className="admin-empty-state">
            <User size={48} />
            <p>Select an account to view entitlements</p>
          </div>
        ) : entitlementsLoading ? (
          <div className="admin-loading">
            <div className="loading-spinner">Loading entitlements...</div>
          </div>
        ) : entitlements.length === 0 ? (
          <div className="admin-empty-state">
            <Flag size={48} />
            <p>No entitlements for this account</p>
            <button
              className="admin-btn admin-btn-primary"
              onClick={() => handleOpenEntitlementModal()}
            >
              <Plus size={16} />
              Add First Entitlement
            </button>
          </div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-data-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Status</th>
                  <th>Source</th>
                  <th>Expires At</th>
                  <th>Created By</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {entitlements.map((entitlement) => {
                  const expired = isExpired(entitlement);
                  return (
                    <tr
                      key={entitlement.id}
                      className={expired ? 'entitlement-expired' : ''}
                    >
                      <td>
                        <div className="admin-table-cell-primary">
                          <code className="feature-flag-key">
                            {entitlement.feature_flag_key}
                          </code>
                          {entitlement.feature_flag_description && (
                            <div className="feature-flag-description-small">
                              {entitlement.feature_flag_description}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        {entitlement.enabled ? (
                          <span className="admin-status-badge admin-status-success">
                            <CheckCircle2 size={12} />
                            <span>Enabled</span>
                          </span>
                        ) : (
                          <span className="admin-status-badge admin-status-danger">
                            <XCircle size={12} />
                            <span>Disabled</span>
                          </span>
                        )}
                        {expired && (
                          <div className="entitlement-expired-badge">
                            <Clock size={12} />
                            <span>Expired</span>
                          </div>
                        )}
                      </td>
                      <td>
                        <span
                          className={`entitlement-source-badge ${getSourceBadgeClass(
                            entitlement.source
                          )}`}
                        >
                          {entitlement.source_display}
                        </span>
                      </td>
                      <td>
                        {entitlement.expires_at ? (
                          <div>
                            {formatDate(entitlement.expires_at)}
                            {expired && (
                              <div className="entitlement-expired-text">(Expired)</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted">Never</span>
                        )}
                      </td>
                      <td>
                        {entitlement.created_by_email || (
                          <span className="text-muted">System</span>
                        )}
                      </td>
                      <td>
                        <div className="admin-action-buttons">
                          <button
                            className="admin-btn admin-btn-secondary"
                            onClick={() => handleOpenEntitlementModal(entitlement)}
                            title="Edit entitlement"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            className="admin-btn admin-btn-danger"
                            onClick={() => handleDeleteEntitlement(entitlement.id)}
                            title="Delete entitlement"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Entitlement Modal */}
      {showEntitlementModal && (
        <EntitlementModal
          entitlement={editingEntitlement}
          featureFlags={featureFlags}
          onSave={handleSaveEntitlement}
          onClose={handleCloseEntitlementModal}
        />
      )}
    </div>
  );
};

/**
 * Modal for creating/editing account entitlements.
 */
const EntitlementModal = ({ entitlement, featureFlags, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    feature_flag: entitlement?.feature_flag || '',
    enabled: entitlement?.enabled !== undefined ? entitlement.enabled : true,
    source: entitlement?.source || 'admin_override',
    expires_at: entitlement?.expires_at
      ? new Date(entitlement.expires_at).toISOString().split('T')[0]
      : '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      feature_flag: formData.feature_flag,
      enabled: formData.enabled,
      source: formData.source,
      expires_at: formData.expires_at || null,
    };
    onSave(data);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{entitlement ? 'Edit Entitlement' : 'Add Entitlement'}</h3>
          <button className="modal-close" onClick={onClose}>
            <XCircle size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="entitlement-form">
          <div className="form-group">
            <label htmlFor="feature_flag">Feature Flag *</label>
            <select
              id="feature_flag"
              value={formData.feature_flag}
              onChange={(e) =>
                setFormData({ ...formData, feature_flag: e.target.value })
              }
              required
              disabled={!!entitlement}
            >
              <option value="">Select a feature flag...</option>
              {featureFlags.map((flag) => (
                <option key={flag.id} value={flag.id}>
                  {flag.key} - {flag.description || 'No description'}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) =>
                  setFormData({ ...formData, enabled: e.target.checked })
                }
              />
              <span>Enabled</span>
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="source">Source *</label>
            <select
              id="source"
              value={formData.source}
              onChange={(e) =>
                setFormData({ ...formData, source: e.target.value })
              }
              required
            >
              <option value="admin_override">Admin Override</option>
              <option value="early_access">Early Access</option>
              <option value="promo">Promotional</option>
              <option value="paid">Paid Add-on</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="expires_at">Expiration Date (Optional)</label>
            <input
              type="date"
              id="expires_at"
              value={formData.expires_at}
              onChange={(e) =>
                setFormData({ ...formData, expires_at: e.target.value })
              }
              min={new Date().toISOString().split('T')[0]}
            />
            <small>Leave empty for no expiration</small>
          </div>

          <div className="modal-actions">
            <button type="button" className="admin-btn admin-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="admin-btn admin-btn-primary">
              {entitlement ? 'Update' : 'Create'} Entitlement
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
